import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../context/admin';
import { NAME_MAX, STANDARD_LAYOUT_ID } from '../../lib/access';
import type { NamedLayout } from '../../lib/access';
import { createLayout, deleteLayout } from '../../lib/api/admin';
import { ActionMessages, AdminShell, ConfirmDelete } from './AdminShell';
import { plural, usePaging, useView } from './listing';
import { Dialog, DialogButtons, DialogError, ListFoot, ViewToggle } from './parts';
import { useAdminAction } from './useAdminAction';

/**
 * The layouts a role can be given. A layout is the sections on the left rail
 * and the tabs inside each: make one per kind of work, then pick it on the
 * role, and everyone with that role sees it. A tab a role has no permission
 * for stays hidden whatever the layout says. The editor for one layout is a
 * page of its own (LayoutAdmin).
 */
export function Layouts() {
  const { store } = useAdmin();
  const { busy, error, notice, run, clear } = useAdminAction();
  const [view, setView] = useView();

  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [use, setUse] = useState<'all' | 'used' | 'unused'>('all');

  /** The roles on each layout; the built-in superadmin is on the standard one. */
  const usedBy = (layout: NamedLayout) => {
    const roles = store.roles.filter((r) => r.layoutId === layout.id).map((r) => r.name);
    if (layout.id === STANDARD_LAYOUT_ID) roles.unshift('Superadmin');
    return roles;
  };

  const filtered = store.layouts.filter((layout) => {
    const q = search.trim().toLowerCase();
    if (q && !layout.name.toLowerCase().includes(q)) return false;
    const n = usedBy(layout).length;
    if (use === 'used' && n === 0) return false;
    if (use === 'unused' && n > 0) return false;
    return true;
  });
  const paging = usePaging(filtered);
  const filter = (apply: () => void) => {
    apply();
    paging.first();
  };

  async function onCreate(name: string, copyOf: number) {
    const ok = await run(() => createLayout(name, copyOf), `${name.trim()} created. Open it to arrange its sections and tabs.`);
    if (ok) setAdding(false);
  }

  const editor = (layout: NamedLayout) => `/web/Admin/Layout?id=${layout.id}`;

  const sections = (layout: NamedLayout) =>
    layout.rails
      .filter((r) => !r.hidden)
      .map((r) => (
        <span key={r.id} className="cmchip">
          <i style={{ background: r.color }} />
          {r.label} · {r.tabs.filter((t) => !t.hidden).length}
        </span>
      ));

  const roles = (layout: NamedLayout) => {
    const names = usedBy(layout);
    return names.length > 0 ? names.join(', ') : <span className="cmmuted">No role yet</span>;
  };

  const actions = (layout: NamedLayout) => (
    <>
      <Link to={editor(layout)} className="cmbtn cmbtn--small">
        Edit sections and tabs
      </Link>
      {layout.id !== STANDARD_LAYOUT_ID && (
        <ConfirmDelete
          question={`Delete ${layout.name}?`}
          busy={busy}
          onDelete={() => void run(() => deleteLayout(layout.id), `${layout.name} deleted.`)}
        />
      )}
    </>
  );

  return (
    <AdminShell
      active="Layouts"
      title="Layout Management"
      count={`${store.layouts.length} ${plural(store.layouts.length, 'Layout')}`}
      action={
        <button
          type="button"
          className="cmbtn cmbtn--primary"
          onClick={() => {
            clear();
            setAdding(true);
          }}
        >
          + Add Layout
        </button>
      }
    >
      <div className="cmtools">
        <input
          type="search"
          className="cmsearch"
          placeholder="Search layout name…"
          aria-label="Search layout name"
          value={search}
          onChange={(e) => filter(() => setSearch(e.target.value))}
        />
        <select
          className="cmselect"
          aria-label="Used by"
          value={use}
          onChange={(e) => filter(() => setUse(e.target.value as 'all' | 'used' | 'unused'))}
        >
          <option value="all">Used by: All</option>
          <option value="used">Used by: A role</option>
          <option value="unused">Used by: No role yet</option>
        </select>

        <span className="cmspacer" />
        <ViewToggle view={view} onChange={setView} />
      </div>

      <ActionMessages error={error} notice={notice} inDialog={adding} />

      {filtered.length === 0 && <p className="cmempty">No layout matches that search.</p>}

      {paging.shown.length > 0 && view === 'list' && (
        <div className="cmtable-wrap">
          <table className="cmtable">
            <thead>
              <tr>
                <th>Layout name</th>
                <th>Sections and tabs</th>
                <th>Used by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paging.shown.map((layout) => (
                <tr key={layout.id}>
                  <td>
                    <div className="cmname">
                      <Link to={editor(layout)} className="cmlink">
                        {layout.name}
                      </Link>
                      {layout.id === STANDARD_LAYOUT_ID && <span className="cmtag cmtag--plain">Standard</span>}
                    </div>
                  </td>
                  <td className="cmwrap">{sections(layout)}</td>
                  <td className="cmwrap">{roles(layout)}</td>
                  <td>
                    <span className="cmactions">{actions(layout)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paging.shown.length > 0 && view === 'grid' && (
        <div className="cmgrid">
          {paging.shown.map((layout) => (
            <article key={layout.id} className="cmcard">
              <header>
                <span>
                  <Link to={editor(layout)} className="cmlink">
                    {layout.name}
                  </Link>
                  <small>
                    {layout.rails.filter((r) => !r.hidden).length} {plural(layout.rails.filter((r) => !r.hidden).length, 'section')}
                  </small>
                </span>
                {layout.id === STANDARD_LAYOUT_ID && <span className="cmtag cmtag--plain">Standard</span>}
              </header>
              <dl>
                <div>
                  <dt>Sections</dt>
                  <dd className="cmchips">{sections(layout)}</dd>
                </div>
                <div>
                  <dt>Used by</dt>
                  <dd>{roles(layout)}</dd>
                </div>
              </dl>
              <footer className="cmactions">{actions(layout)}</footer>
            </article>
          ))}
        </div>
      )}

      <ListFoot paging={paging} one="layout" />

      {adding && (
        <LayoutDialog
          layouts={store.layouts}
          busy={busy}
          error={error}
          onClose={() => setAdding(false)}
          onCreate={(name, copyOf) => void onCreate(name, copyOf)}
        />
      )}
    </AdminShell>
  );
}

function LayoutDialog({
  layouts,
  busy,
  error,
  onClose,
  onCreate,
}: {
  layouts: NamedLayout[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (name: string, copyOf: number) => void;
}) {
  const [name, setName] = useState('');
  const [copyOf, setCopyOf] = useState(STANDARD_LAYOUT_ID);

  function submit(e: FormEvent) {
    e.preventDefault();
    onCreate(name, copyOf);
  }

  return (
    <Dialog
      title="New Layout"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} label="Create Layout" onClose={onClose} />}
    >
      <DialogError error={error} />

      <label className="cmfield">
        <span>Layout Name *</span>
        <input
          type="text"
          value={name}
          maxLength={NAME_MAX}
          required
          autoFocus
          placeholder="e.g. Geology"
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <label className="cmfield">
        <span>Start As A Copy Of</span>
        <select value={copyOf} onChange={(e) => setCopyOf(Number(e.target.value))}>
          {layouts.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </label>

      <p className="cminfo">
        A new layout starts as a copy. Open it afterwards to rename, reorder, hide or add its sections and tabs, then
        pick it on a role: everyone with that role sees it.
      </p>
    </Dialog>
  );
}
